import { useEffect, useRef, useCallback, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');

export function useWebSocket(runId, { onLog, onStepUpdate, onStatusUpdate, onValidation } = {}) {
  const wsRef = useRef(null);
  const reconnectRef = useRef(0);
  const heartbeatRef = useRef(null);
  const [connected, setConnected] = useState(false);

  // Store callbacks in refs to avoid re-creating the connection
  const onLogRef = useRef(onLog);
  const onStepUpdateRef = useRef(onStepUpdate);
  const onStatusUpdateRef = useRef(onStatusUpdate);
  const onValidationRef = useRef(onValidation);

  useEffect(() => { onLogRef.current = onLog; }, [onLog]);
  useEffect(() => { onStepUpdateRef.current = onStepUpdate; }, [onStepUpdate]);
  useEffect(() => { onStatusUpdateRef.current = onStatusUpdate; }, [onStatusUpdate]);
  useEffect(() => { onValidationRef.current = onValidation; }, [onValidation]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const token = localStorage.getItem('token');
    const wsUrl = `${WS_URL}/ws${runId ? `/${runId}` : ''}${token ? `?token=${token}` : ''}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectRef.current = 0;

        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'ping' })); } catch {}
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'log':
              onLogRef.current?.(data);
              break;
            case 'step_update':
              onStepUpdateRef.current?.(data);
              break;
            case 'run_status_update':
              onStatusUpdateRef.current?.(data);
              break;
            case 'validation_required':
              onValidationRef.current?.(data);
              break;
            default:
              break;
          }
        } catch {}
      };

      ws.onclose = (event) => {
        setConnected(false);
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
        if (!event.wasClean && reconnectRef.current < 10) {
          reconnectRef.current++;
          const delay = Math.min(3000 * Math.pow(1.5, reconnectRef.current - 1), 30000);
          setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {};
    } catch {}
  }, [runId]);

  const disconnect = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const send = useCallback((type, payload = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...payload }));
    }
  }, []);

  useEffect(() => {
    if (runId) {
      connect();
    }
    return () => disconnect();
  }, [runId, connect, disconnect]);

  return { connected, send, disconnect };
}
